import { FC } from "react";
import { Tag } from "antd";
import classNames from "classnames";
import { MdCheck, MdContactMail, MdEmail, MdPhone } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { GuestFormValues } from "src/Modules/Hotels/constants";
import { useHotelDynamicDetails } from "../../../../context";

const GuestChip: FC<{
  guest: GuestFormValues;
  guestNumber: number;
  isPrimary: boolean;
  guestType: string | null;
}> = ({
  guest,
  guestNumber,
  isPrimary,
  guestType,
}) => {
  const { t } = useTranslation();
  const fullName = [guest.title, guest.firstName, guest.lastName].filter(Boolean).join(" ");

  return (
    <div className="outline-border-subtle outline flex w-full flex-wrap items-center justify-between gap-2 rounded-sm p-4 py-6">
      <div className="flex items-center gap-2">
        <div
          className={classNames(
            "flex aspect-square h-10 w-10 items-center justify-center rounded-sm p-2 text-base font-semibold bg-background-primary",
          )}
        >
          <div className={classNames("uppercase text-white")}>G{guestNumber}</div>
        </div>
        <div className="flex flex-col justify-center">
          <div className="text-font-subtle text-xs">{isPrimary ? t("Hotels.guests.primaryGuest") : t("Hotels.guests.guest")}</div>
          <div className="text-font-default -m-0.5 text-base font-medium">{fullName || "—"}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {guestType && <Tag className="rounded-full! px-3! py-1! text-xs! font-semibold! capitalize!">{guestType}</Tag>}
        {isPrimary && (
          <div
            
            className="p-1! px-5! rounded-full! flex! items-center! gap-2 text-font-inverted! bg-background-primary text-xs"
          >
            <div><MdCheck className="w-4 h-4" /></div>
            {t("Flights.details.passengerCard.primaryContact")}
          </div>
        )}
      </div>
    </div>
  );
};

export default function GuestSummaryContent() {
  const { t } = useTranslation();
  const { guestsState, primaryGuestKeyState } = useHotelDynamicDetails();
  const [primaryGuestKey] = primaryGuestKeyState;
  const [guests] = guestsState;
  const entries = Object.entries(guests).filter(([, g]) => g.firstName || g.lastName);

  const rooms = entries.reduce<Record<string, [string, GuestFormValues][]>>((acc, entry) => {
    const [key] = entry;
    const roomNumber = key.match(/^room_(\d+)/)?.[1] ?? "0";
    (acc[roomNumber] ??= []).push(entry);
    return acc;
  }, {});

  const primaryGuest = primaryGuestKey ? guests[primaryGuestKey] : undefined;

  return (
    <div className="flex w-full flex-col gap-5 p-4 py-6">
      {Object.entries(rooms).map(([roomNumber, roomEntries]) => {
        const primaryEntry = roomEntries.find(([key]) => key === primaryGuestKey);
        const otherEntries = roomEntries.filter(([key]) => key !== primaryGuestKey);

        return (
          <div key={roomNumber} className="flex flex-col gap-2">
            <div className="text-font-subtle text-sm font-semibold">{t("Hotels.search.roomNumber", { number: Number(roomNumber) + 1 })}</div>
            <div className="flex w-full flex-wrap gap-2 md:flex-row flex-col p-2 md:p-0">
              {primaryEntry && (
                <div className="w-full">
                  <GuestChip
                    guest={primaryEntry[1]}
                    guestNumber={entries.findIndex(([key]) => key === primaryEntry[0]) + 1}
                    guestType={primaryEntry[0].match(/_(adult|child)_/)?.[1] ?? null}
                    isPrimary
                  />
                </div>
              )}
              {otherEntries.map(([key, guest]) => (
                <div key={key} className="md:w-[calc(50%-0.25rem)] w-full shrink-0 grow-0">
                  <GuestChip
                    guest={guest}
                    guestNumber={entries.findIndex(([entryKey]) => entryKey === key) + 1}
                    guestType={key.match(/_(adult|child)_/)?.[1] ?? null}
                    isPrimary={false}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {primaryGuest?.contact && (primaryGuest.contact.email || primaryGuest.contact.phone) && (
        <>
          <div className="bg-border-subtle h-px w-full" />
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <MdContactMail className="h-5 w-5 text-icon-active" />
              <div className="text-lg font-semibold">{t("Flights.details.contact.contactInformation")}</div>
            </div>
            <div className="bg-background-minimal flex flex-wrap items-start justify-between gap-4 rounded-sm p-4">
              {primaryGuest.contact.email && (
                <div className="flex flex-1 flex-col gap-0.5">
                  <div className="text-font-subtle flex items-center gap-2 text-xs uppercase">
                    <MdEmail /> {t("Flights.details.contact.primaryContactEmail")}
                  </div>
                  <div className="text-font-default truncate text-base font-semibold px-4" title={primaryGuest.contact.email}>
                    {primaryGuest.contact.email}
                  </div>
                </div>
              )}
              {primaryGuest.contact.phone && (
                <div className="flex flex-1 flex-col gap-0.5">
                  <div className="text-font-subtle flex items-center gap-2 text-xs uppercase">
                    <MdPhone /> {t("Flights.details.contact.mobilePhone")}
                  </div>
                  <div className="text-font-default text-base font-semibold px-4">{primaryGuest.contact.phone}</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
